import { asyncAction } from "../common"
import { limsRequest } from "./limsRequest"
import { rsd } from "./calcEngine"
import { selectSstSpec } from "./limsSstSpec"

// Per-run System Suitability Test result (PRD-005 Area 3 / SPEC-017; SPEC-021 cutover: persisted
// via the REAL /lims/sstResults endpoints). An SST is performed PER analysis-run: the analyst
// enters the (typically six) injections' AREA and RETENTION TIME plus tailing factor, plate count
// (N) and resolution (Rs) from the instrument report. The SERVER is authoritative (SP-E Phase
// 3.2): it recomputes the RSDs from the stored injections, re-judges every criterion against the
// live SST spec and stamps enteredBy/enteredAt + the audit entries. The FE judgement below
// (computeRsd/judgeSst) is kept as LIVE feedback while typing.
//
// The analyst e-signs the SST via PATCH /lims/sstResults/<id> with { password, reason } at the
// request-body TOP LEVEL (SPEC-021 B1 — the backend reads them directly, NOT {patch:{signature}});
// the server verifies the password, requires the "analyst" capability and stamps the immutable
// signature. 403 on a failed gate.
//
// Record shape (server dump):
//   {
//     id,                            // = analysisId (upsert-by-id, one SST per run)
//     analysisId, analysisDefinitionId,
//     injections: [{ area, rt }],    // the analyst's entered replicate injections
//     computed: { rsdArea, rsdRt },  // server-computed RSDs (%)
//     tailing, plateCount, resolution,
//     perParam: { rsdArea, rsdRt, tailing, plateCount, resolution }, // true|false|null per criterion
//     overall: "PASS" | "FAIL" | null,
//     signature,                     // server-stamped analyst e-signature, set by signSst
//     overrideSignature,             // legacy display field (gate overrides now ride on the
//                                    // results send-to-review PATCH — see limsResults.sendToReview)
//     enteredBy, enteredAt
//   }

const initialState = { items: [], isLoading: false }

export function reducer(state = initialState, action) {
  switch (action.type) {
    case "limsSstResult/fetch/BEGIN":
      return { ...state, isLoading: true }
    case "limsSstResult/fetch":
      return { items: action.res, isLoading: false }
    case "limsSstResult/save":
    case "limsSstResult/sign": {
      const item = action.res
      if (!item) return state
      const exists = state.items.some((x) => x.id === item.id)
      return {
        ...state,
        items: exists ? state.items.map((x) => (x.id === item.id ? item : x)) : [...state.items, item],
      }
    }
    case "auth/logout":
      return initialState
    default:
      return state
  }
}

// ---- Judgement helpers (pure; LIVE feedback — the server re-judges on save) -------------------

// Locale-robust numeric coercion (Danish decimal comma tolerated). Returns NaN for blank/garbage.
function toNumber(x) {
  if (x === "" || x === null || x === undefined) return NaN
  return Number(typeof x === "string" ? x.replace(",", ".") : x)
}

// Compute the RSD (%) of one injection column (area or rt). Returns null when there are fewer than
// two finite values (RSD is undefined for n<2) — reuses the SPEC-016 engine's `rsd`.
export function computeRsd(injections, key) {
  const values = (Array.isArray(injections) ? injections : [])
    .map((inj) => toNumber(inj && inj[key]))
    .filter((n) => Number.isFinite(n))
  if (values.length < 2) return null
  const r = rsd(values)
  return Number.isFinite(r) ? r : null
}

// Judge a computed/entered value against a "≤ max" criterion. null bound → not judged (null).
function judgeMax(value, max) {
  const v = toNumber(value)
  const m = toNumber(max)
  if (Number.isNaN(v) || Number.isNaN(m)) return null
  return v <= m
}

// Judge a value against a "≥ min" criterion. null bound → not judged (null).
function judgeMin(value, min) {
  const v = toNumber(value)
  const m = toNumber(min)
  if (Number.isNaN(v) || Number.isNaN(m)) return null
  return v >= m
}

// Judge a value against a min/max range. When only one bound is set, that single bound is applied;
// when neither is set, the parameter is not judged (null).
function judgeRange(value, min, max) {
  const v = toNumber(value)
  if (Number.isNaN(v)) return null
  const minN = toNumber(min)
  const maxN = toNumber(max)
  const hasMin = !Number.isNaN(minN)
  const hasMax = !Number.isNaN(maxN)
  if (!hasMin && !hasMax) return null
  if (hasMin && v < minN) return false
  if (hasMax && v > maxN) return false
  return true
}

// Judge a whole SST record against an SST spec. Returns the perParam map + the derived overall.
// Each parameter is true (pass) / false (fail) / null (not judged — criterion unset or value not
// yet entered). The overall is FAIL if any judged parameter fails, PASS if at least one parameter
// is judged and none fail, and null when nothing is judged yet.
export function judgeSst(record, spec) {
  const safeSpec = spec || {}
  const computed = (record && record.computed) || {}
  const perParam = {
    rsdArea: judgeMax(computed.rsdArea, safeSpec.rsdAreaMax),
    rsdRt: judgeMax(computed.rsdRt, safeSpec.rsdRtMax),
    tailing: judgeRange(record && record.tailing, safeSpec.tailingMin, safeSpec.tailingMax),
    plateCount: judgeMin(record && record.plateCount, safeSpec.plateCountMin),
    resolution: judgeMin(record && record.resolution, safeSpec.resolutionMin),
  }
  const judged = Object.values(perParam).filter((v) => v === true || v === false)
  let overall = null
  if (judged.length > 0) overall = judged.every((v) => v === true) ? "PASS" : "FAIL"
  return { perParam, overall }
}

// Build the SST record sent on save from the analyst's raw entry + the spec. The locally
// computed/judged fields ride along for the live UI only — the server recomputes + re-judges
// them authoritatively. `entry` is { injections, tailing, plateCount, resolution }.
export function buildSstRecord({ analysisId, analysisDefinitionId, entry, spec }) {
  const injections = (entry && Array.isArray(entry.injections) ? entry.injections : []).map((inj) => ({
    area: inj && inj.area !== undefined && inj.area !== null ? inj.area : "",
    rt: inj && inj.rt !== undefined && inj.rt !== null ? inj.rt : "",
  }))
  const computed = {
    rsdArea: computeRsd(injections, "area"),
    rsdRt: computeRsd(injections, "rt"),
  }
  const base = {
    analysisId,
    analysisDefinitionId: analysisDefinitionId || null,
    injections,
    computed,
    tailing: entry && entry.tailing !== undefined ? entry.tailing : "",
    plateCount: entry && entry.plateCount !== undefined ? entry.plateCount : "",
    resolution: entry && entry.resolution !== undefined ? entry.resolution : "",
  }
  const { perParam, overall } = judgeSst(base, spec)
  return {
    id: analysisId,
    ...base,
    perParam,
    overall,
  }
}

// ---- Thunks ----------------------------------------------------------------------------------

export const fetch = asyncAction("limsSstResult/fetch", (_dispatch) => {
  return limsRequest("GET", "/lims/sstResults").then((res) => res.items)
})

// Save (upsert-by-id) the per-run SST record. The server recomputes the RSDs, re-judges every
// criterion, stamps enteredBy/enteredAt on first save and writes the audit entry. `record` is a
// record from buildSstRecord. Returns the saved (server-judged) record.
export const saveSst = asyncAction("limsSstResult/save", (_dispatch, record) => {
  return limsRequest("POST", "/lims/sstResults", { input: record }).then((res) => res.item)
})

// Sign the SST (the analyst's self sign-off). `signature` is the ESignatureModal payload; its
// password + reason are sent at the request-body TOP LEVEL (the backend contract — there is no
// {patch:{signature}} envelope). The server verifies the password, requires the "analyst"
// capability and stamps the immutable signature + audit entry. Returns the updated record.
export const signSst = asyncAction("limsSstResult/sign", (_dispatch, id, signature) => {
  return limsRequest("PATCH", `/lims/sstResults/${id}`, {
    password: (signature && signature.password) || undefined,
    reason: (signature && signature.reason) || undefined,
  }).then((res) => res.item)
})

// ---- Selectors -------------------------------------------------------------------------------

// The stored SST record for one analysis run (or undefined).
export const selectSstResult = (state, analysisId) =>
  analysisId ? state.limsSstResult.items.find((x) => x.analysisId === analysisId) : undefined

// Whether the SST for one analysis run has PASSED and been SIGNED — the condition the send-to-
// review hard gate checks. Re-judges the stored record against the CURRENT spec so a criteria
// change is reflected without re-saving (the server-side gate does the same).
export const isSstPassedAndSigned = (state, analysisId) => {
  const record = selectSstResult(state, analysisId)
  if (!record || !record.signature) return false
  const spec = selectSstSpec(state, record.analysisDefinitionId)
  const overall = spec ? judgeSst(record, spec).overall : record.overall
  return overall === "PASS"
}
